import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { Button, Badge, Form } from "react-bootstrap";
import { fetchCompetitiveSubjects, deleteCompetitiveSubject } from "../../redux/slices/competitiveSubjectSlice";
import { fetchExams } from "../../redux/slices/examSlice";
import CompetitiveSubjectModal from "./CompetitiveSubjectModal";
import DynamicBadge from "../Common/DynamicBadge";
import ConfirmModal from "../Common/ConfirmModal";
import InfoPill from "../Common/InfoPill";
import { FaPencilAlt, FaRegTrashAlt, FaImage } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const SafeImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  return hasError || !src ? (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "4px",
        backgroundColor: "#f8f9fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #dee2e6"
      }}
    >
      <FaImage className="text-secondary" style={{ fontSize: "1.1rem" }} />
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      style={{
        width: "40px",
        height: "40px",
        objectFit: "cover",
        borderRadius: "4px",
        border: "1px solid #dee2e6"
      }}
      onError={() => setHasError(true)}
    />
  );
};

const TABLE_HEADERS = [
  { prop: "subject_name", title: "Subject Name", isSortable: true, isFilterable: true },
  { 
    prop: "image_url", 
    title: "Image", 
    cell: (row) => <SafeImage src={row.image_url} alt={row.subject_name} /> 
  },
  { 
    prop: "exam_name", 
    title: "Exam", 
    isSortable: true,
    cell: (row) => <DynamicBadge text={row.exam_name || "Unknown"} />
  },
  { 
    prop: "status", 
    title: "Status", 
    isSortable: true, 
    cell: (row) => <Badge bg={row.status === "Active" ? "success" : "danger"}>{row.status}</Badge> 
  },
  { 
    prop: "createddate", 
    title: "Created Date", 
    isSortable: true, 
    cell: (row) => formatDateToDDMMYYYY(row.createddate) 
  },
  {
    prop: "actions",
    title: "Actions",
    cell: (row) => (
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-dark"><FaPencilAlt /></Button>
        <Button size="sm" variant="outline-danger"><FaRegTrashAlt /></Button>
      </div>
    ),
  },
];

const CompetitiveSubjects = () => {
  const dispatch = useDispatch();
  const { competitiveSubjects, isLoading } = useSelector((state) => state.competitiveSubjects);
  const { exams } = useSelector((state) => state.exams);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [examFilter, setExamFilter] = useState("");

  useEffect(() => {
    dispatch(fetchCompetitiveSubjects());
    dispatch(fetchExams());
  }, [dispatch]);

  const handleEdit = (id) => {
    setEditingId(id);
    setShowModal(true);
  };

  const handleDelete = (id, name) => {
    setItemToDelete({ id, name });
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await dispatch(deleteCompetitiveSubject(itemToDelete.id)).unwrap();
        toast.success(`Subject "${itemToDelete.name}" deleted`);
        setShowConfirmModal(false);
      } catch (error) {
        toast.error(error || "Delete failed");
      }
    }
  };

  const filteredSubjects = examFilter 
    ? competitiveSubjects.filter(s => s.exam_id === examFilter)
    : competitiveSubjects;

  const tableHeaders = React.useMemo(() => TABLE_HEADERS.map(h => {
    if (h.prop === "actions") {
      return {
        ...h,
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-dark" onClick={() => handleEdit(row.id)}><FaPencilAlt /></Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(row.id, row.subject_name)}><FaRegTrashAlt /></Button>
          </div>
        )
      };
    }
    return h;
  }), []);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading subjects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <CompetitiveSubjectModal show={showModal} handleClose={() => { setShowModal(false); setEditingId(null); }} editId={editingId} />
      <ConfirmModal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)} 
        onConfirm={confirmDelete} 
        title="Delete Subject" 
        message={`Are you sure you want to delete "${itemToDelete?.name}"?`}
        variant="danger"
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i className="fa-solid fa-chevron-right" style={{ marginLeft: "1px", marginRight: "1px" }}></i>{" "}
          Competitive Subjects
        </Link>
      </div>

      <DatatableWrapper 
        body={filteredSubjects || []} 
        headers={tableHeaders || []} 
        paginationOptionsProps={{ initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] } }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
              <Filter placeholder="Search subjects..." className="w-100" />
            </div>
            
            <div style={{ maxWidth: "200px" }}>
              <Form.Select 
                value={examFilter} 
                onChange={(e) => setExamFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Exams</option>
                {exams.map(e => <option key={e.id} value={e.id}>{e.exam_name}</option>)}
              </Form.Select>
            </div>
            
            <PaginationOptions />
            <InfoPill left="Total Subjects" right={filteredSubjects?.length || 0} />
          </div>

          <div className="d-flex align-items-center">
            <Button variant="dark" className="btn-sm" onClick={() => setShowModal(true)}>
              New Subject
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default CompetitiveSubjects;
